import {
    FieldHelperProps,
    FieldInputProps,
    FieldMetaProps,
    FormikConfig,
    FormikProps,
    FormikProvider,
    FormikValues,
    useFormikContext,
} from 'formik';
import { cloneDeep, get, kebabCase } from 'lodash';
import { CSSProperties, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import CardList from 'views/declarations/common/list-card/CardList';
import NewFormCard from './cards/NewFormCard';
import FormCard from './cards/FormCard';
import ConditionalWrapper from 'components/ConditionalWrapper';
import { useTemplateContext } from 'components/ui/composed/template/TemplateContext';
import { ButtonProps, Divider } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import addPathPrefix from 'utils/addPathPrefix';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useProducts from 'hooks/useProducts';
import { useDeclarationContext } from 'views/declarations/common/DeclarationContext';
import useDeclarations from 'hooks/useDeclarations';
import { useLocation, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { Events } from 'utils/events.enum';
import { SBox44Button } from './box44/components/styles';
import PlusIcon from './box44/components/PlusIcon';
import { setFieldValueAndTouch } from './utils/setFieldValueAndTouch';

const MicWrapper = styled.div<{ formIsVisible: boolean }>`
    position: ${({ formIsVisible }) => (formIsVisible ? 'static' : 'absolute')};
    opacity: ${({ formIsVisible }) => (formIsVisible ? 1 : 0)};
    top: ${({ formIsVisible }) => (formIsVisible ? 'auto' : '-9999px')};
    left: ${({ formIsVisible }) => (formIsVisible ? 'auto' : '-9999px')};
`;

const AddItemButton = (props: ButtonProps): ReactElement => {
    return (
        <SBox44Button {...props}>
            <PlusIcon /> Add Item
        </SBox44Button>
    );
};

export interface FormProps {
    getFieldProps: (name: string) => FieldInputProps<any>;
    getFieldHelpers: (name: string) => FieldHelperProps<any>;
    getFieldMeta: (name: string) => FieldMetaProps<any>;
}

type DataList = { field: string; value: string | undefined }[][];

interface Props<TValue extends FormikValues> extends Partial<DeclarationFormCardProps> {
    initialValues: FormikConfig<TValue>['initialValues'];
    validationSchema?: FormikConfig<TValue>['validationSchema'];
    path: string;
    title: string;
    list: (obj: TValue) => { field: string; value?: string | ReactNode[] | string[] }[];
    form?: (props: FormProps, path?: string) => ReactElement;
    children?: (path: string | null) => ReactNode;
    required?: boolean;
    formik?: FormikProps<any>;
    condensed?: boolean;
    onAdd?: (value: TValue) => void;
    onEdit?: (value: TValue) => void;
    onDelete?: (index: number) => void;
    beforeDelete?: (index: number) => void | false;
    refNumber?: string | string[];
    requiredFields?: string[];
    cardless?: boolean;
    section?: boolean;
    style?: CSSProperties;
    ctaButtons?: (micCardIndex: number | undefined) => JSX.Element[];
}

const MultipleItemsCard = <TValue extends FormikValues>(props: Props<TValue>) => {
    const location = useLocation();
    const inViewOnly = location.pathname.includes('view-only');
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const { form } = useDeclarationContext();
    const { declaration } = useDeclarations();
    const { form: templateForm, template, templateFormik, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { products } = useProducts();
    const formik = useFormikContext<any>();

    const [status, setStatus] = useState<'IDLE' | 'ADDING' | 'EDITING'>('IDLE');
    const [recordToEdit, setRecordToEdit] = useState<{ index: number; value: any }>();
    const [dataList, setDataList] = useState<DataList | null>(null);

    const convertToDataList = useCallback(
        (data: any[] | undefined | null) => (Array.isArray(data) ? (data?.map(props.list) as DataList) : []),
        [props.list]
    );

    const path = useMemo(
        () => (template ? addPathPrefix(`${templateForm}.defaults`, props.path) : props.path),
        [props.path, template, templateForm]
    );

    const formIsVisible = useMemo(() => status === 'EDITING' || status === 'ADDING', [status]);

    const formikToUse = useMemo(
        () => (template && templateFormik ? templateFormik : formik),
        [formik, template, templateFormik]
    );

    const items: any[] = useMemo(
        () => formikToUse.getFieldProps(path)?.value ?? [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formikToUse.getFieldProps(path)?.value]
    );

    const isViewOnly = useMemo(() => {
        if (outletContext?.amendment) return false;
        return props.viewOnly || inViewOnly;
    }, [props.viewOnly, inViewOnly, outletContext?.amendment]);

    const updateDataList = () => {
        setDataList(convertToDataList(items));
    };

    const deleteTemplateMeta = useCallback(
        (index: number) => {
            const meta = cloneDeep(templateFormik?.getFieldProps(`${templateForm}.meta`).value);
            const fieldMeta = Object.fromEntries(Object.entries(meta).filter(([key]) => key.startsWith(props.path)));

            // Delete all the meta data for the field
            Object.keys(fieldMeta).forEach((key) => {
                delete meta[key];
            });

            // Delete the meta data for the deleted item
            const keys = Object.keys(fieldMeta).filter((key) => key.startsWith(`${props.path}.${index}`));
            keys.forEach((key) => {
                delete fieldMeta[key];
            });

            // Rearrange the meta data for the remaining items
            const newMeta = Object.fromEntries(
                Object.entries(fieldMeta).map(([key, value], i) => {
                    const keySplit = key.split('.');
                    const currentIndex = parseInt(keySplit[keySplit.length - 2]);
                    if (currentIndex < index) return [key, value];
                    keySplit[keySplit.length - 2] = `${currentIndex - 1}`;
                    return [keySplit.join('.'), value];
                })
            );

            templateFormik?.setFieldValue(`${templateForm}.meta`, { ...meta, ...newMeta });
        },
        [props.path, templateForm, templateFormik]
    );

    const triggerAddFlow = () => {
        if (status === 'ADDING') return;
        setStatus('ADDING');
        if (recordToEdit !== undefined) setRecordToEdit(undefined);
        const newItems = [...items];
        newItems.push(props.initialValues);

        formikToUse.setFieldValue(path, newItems);

        // Template handle
        if (template) {
            const meta = cloneDeep(templateFormik?.getFieldProps(`${templateForm}.meta`).value);
            const fields = Object.keys(props.initialValues);

            fields.forEach((field) => {
                meta[`${props.path}.${newItems.length - 1}.${field}`] = { isViewable: true, isEditable: true };
            });

            templateFormik?.setFieldValue(`${templateForm}.meta`, meta);
        }
    };
    const triggerEditFlow = (index: number) => {
        if (status === 'ADDING') {
            const newItems = [...items];
            newItems.pop();
            formikToUse.setFieldValue(path, newItems);
        }

        setStatus('EDITING');
        setRecordToEdit({
            index,
            value: formikToUse.getFieldProps(addPathPrefix(path, index.toString()))?.value,
        });
    };
    const cancelFlow = useCallback(() => {
        setStatus('IDLE');
        setRecordToEdit(undefined);
        const newItems = [...items];
        if (status === 'EDITING' && recordToEdit) newItems[recordToEdit?.index] = recordToEdit?.value;
        else if (status === 'ADDING') {
            if (template) deleteTemplateMeta(newItems.length - 1);
            newItems.pop();
        }
        formikToUse.setFieldValue(path, newItems);
        return newItems;
    }, [items, status, recordToEdit, formikToUse, path, template, deleteTemplateMeta]);

    const deleteRecord = (index: number) => {
        const values = [...items];
        values.splice(index, 1);

        setFieldValueAndTouch(formikToUse, path, values);

        props.onDelete?.(index);

        if (index === recordToEdit?.index) {
            setRecordToEdit(undefined);
            setStatus('IDLE');
        }

        /**
         * If deleting an item while adding we don't want to show
         * the added record in the data list so it is removed
         * from the data list but stays in the data itself
         */
        const newValues = [...values];
        if (status === 'ADDING') {
            newValues.pop();
        }

        if (template) deleteTemplateMeta(index);

        setDataList(convertToDataList(newValues));
    };

    const handleAddEditButton = useCallback(async () => {
        setStatus('IDLE');
        const list = formikToUse.getFieldProps(path)?.value;
        setDataList(convertToDataList(list));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convertToDataList, formikToUse.getFieldProps(path)?.value]);

    useEffect(() => {
        const subscriberId = window.EventBus.subscribe(Events.AFTER_DECLARATION_SAVE, () => {
            handleAddEditButton();
        });
        return () => window.EventBus.unsubscribe(subscriberId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleAddEditButton]);

    /**
     * Update data list when the products or declaration changes
     */
    useEffect(() => {
        if (status === 'IDLE') {
            updateDataList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, form, declaration, items]);

    const regularFormPathIndex = useMemo(
        (): number => (status === 'EDITING' ? recordToEdit?.index : items.length ? items.length - 1 : 0) ?? 0,
        [items.length, recordToEdit?.index, status]
    );

    const regularForm = useMemo(() => {
        const micPath = addPathPrefix(props.path, regularFormPathIndex.toString());
        return (
            <FormikProvider value={formikToUse}>
                {props.form ? props.form(formikToUse, micPath) : props.children?.(micPath)}
            </FormikProvider>
        );
    }, [formikToUse, props, regularFormPathIndex]);

    const { hasFormDeclarationErrors } = useDeclarationFormErrors();

    /**
     * Only report what the form holds once a validation run has put something in the store - until
     * then the errors of a form that validates on mount would mark a card the user has not reached.
     * Every level the parsers report counts, not only the master details and the single item list:
     * ICS2 reports its master items and house consignments on their own, and a card of a house
     * consignment would otherwise stay unmarked whenever the master level passes.
     */
    const errors = useMemo(() => {
        if (!hasFormDeclarationErrors) return [];
        return get(formikToUse.errors, path);
    }, [hasFormDeclarationErrors, formikToUse.errors, path]);

    const cardButtons = useMemo(() => {
        if (isViewOnly) return null;

        const buttons: JSX.Element[] = [];

        if (!isTemplateViewOnly && props.ctaButtons && formIsVisible) {
            const ctaButtons = props.ctaButtons(regularFormPathIndex);
            ctaButtons.forEach((button) => buttons.push(button));
        }

        if (!isTemplateViewOnly)
            buttons.push(
                <AddItemButton
                    key={`add-item-button-${path}`}
                    id={`${kebabCase(props.title)}-add-item-button`}
                    onClick={triggerAddFlow}
                />
            );

        return buttons;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isViewOnly, path, props.title, template, isTemplateViewOnly, formIsVisible, props.ctaButtons, items]);

    if (props.hidden) return null;

    return (
        <ConditionalWrapper
            condition={!props.condensed && !props.cardless}
            wrapper={(children) => (
                <FormCard
                    defaultOpen={props.defaultOpen}
                    viewOnly={isViewOnly}
                    cardNumber={props.cardNumber}
                    expandAll={props.expandAll}
                    total={props.cardTotal}
                    keyCard={props.keyCard}
                    title={props.title}
                >
                    {children}
                </FormCard>
            )}
        >
            <ConditionalWrapper
                condition={props.condensed && !props.cardless}
                wrapper={(children) => (
                    <NewFormCard
                        hidden={props.hidden}
                        title={props.title}
                        section={props.section}
                        mic={{ path: path, required: props.required }}
                        refNumber={props.refNumber}
                        cardButtons={cardButtons}
                        error={typeof errors === 'string' ? errors : undefined}
                    >
                        {children}
                    </NewFormCard>
                )}
            >
                <>
                    <MicWrapper formIsVisible={!!formIsVisible}>
                        {formIsVisible ? regularForm : null}
                        {!isViewOnly && (
                            <div
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    justifyContent: 'flex-end',
                                    gap: '1rem',
                                    marginTop: '1rem',
                                }}
                            >
                                <SBox44Button
                                    onClick={cancelFlow}
                                    type="ghost"
                                    size="small"
                                    id={`${kebabCase(props.title)}-cancel-button`}
                                >
                                    Cancel <CloseOutlined height={'min-content'} />
                                </SBox44Button>
                                <SBox44Button
                                    onClick={handleAddEditButton}
                                    size="small"
                                    id={`${kebabCase(props.title)}-${!!recordToEdit ? 'edit' : 'add'}-button`}
                                >
                                    {recordToEdit !== undefined ? (
                                        <>
                                            Confirm <CheckOutlined />
                                        </>
                                    ) : (
                                        <>
                                            Add <CheckOutlined />
                                        </>
                                    )}
                                </SBox44Button>
                            </div>
                        )}
                        <Divider style={{ marginTop: '1rem' }} />
                    </MicWrapper>

                    <div style={{ marginTop: dataList?.length ? '1rem' : 0 }}>
                        <CardList
                            data={dataList ?? []}
                            path={path}
                            errors={errors as any[]}
                            onDelete={(index: number) => {
                                if (props.beforeDelete?.(index) === false) return;
                                deleteRecord(index);
                            }}
                            onEdit={triggerEditFlow}
                            condensed
                            viewOnly={(isViewOnly || isTemplateViewOnly) ?? false}
                            cardTitle={kebabCase(props.title)}
                        />
                    </div>
                </>
            </ConditionalWrapper>
        </ConditionalWrapper>
    );
};
export default MultipleItemsCard;
